export const parseUtc = (utcString) => {
  if (!utcString) return null;
  const str = String(utcString).trim();
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(str)
    ? str.replace(" ", "T") + "Z"
    : str;
  const date = new Date(normalized);
  return isNaN(date) ? null : date;
};

// Converts a UTC datetime string to the user's local time formatted as "12:30 AM" / "10:30 PM".
// Accepts "YYYY-MM-DD HH:MM:SS" (server format) or ISO 8601 strings.
export const formatUtcToLocalTime = (utcString) => {
  if (!utcString) return "—";
  const str = String(utcString).trim();
  // If not a "YYYY-MM-DD HH:MM:SS" server datetime, it's already a formatted local time
  if (!/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(str)) return str;
  const date = parseUtc(str);
  if (!date) return "—";
  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// Returns the user's local date as "YYYY-MM-DD" from a UTC datetime string.
export const getLocalDateFromUtc = (utcString) => {
  const date = parseUtc(utcString);
  if (!date) return null;
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const pad2 = (n) => String(n).padStart(2, "0");

const formatLocalDateMMDDYYYY = (date) =>
  `${pad2(date.getMonth() + 1)}/${pad2(date.getDate())}/${date.getFullYear()}`;

// Converts a UTC datetime string to the user's local date + time,
// formatted as "05/25/2026 08:45 PM".
export const formatUtcToLocalDateTime = (utcString) => {
  const date = parseUtc(utcString);
  if (!date) return "—";
  let hours = date.getHours();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${formatLocalDateMMDDYYYY(date)} ${pad2(hours)}:${pad2(date.getMinutes())} ${ampm}`;
};

// Converts a UTC datetime string to the user's local date, formatted as "05/25/2026".
export const formatUtcToLocalDate = (utcString) => {
  const date = parseUtc(utcString);
  if (!date) return "—";
  return formatLocalDateMMDDYYYY(date);
};

// Formats any date-like input as "MM/DD/YYYY".
// Handles plain "YYYY-MM-DD" (no timezone conversion), "YYYY-MM-DD HH:MM:SS" UTC datetimes
// (converted to local), and anything Date can parse.
export const formatDateMMDDYYYY = (input) => {
  if (!input) return "—";
  const str = String(input).trim();
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (dateOnly) {
    const [, yyyy, mm, dd] = dateOnly;
    return `${mm}/${dd}/${yyyy}`;
  }
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(str)) {
    return formatUtcToLocalDate(str);
  }
  const date = new Date(str);
  if (isNaN(date)) return "—";
  return formatLocalDateMMDDYYYY(date);
};

// Resolves a chat message timestamp to a local Date.
// UTC server datetimes are converted to local; time-only / unknown strings fall back to "now".
const resolveLocalDateFromInput = (input) => {
  const str = String(input || "").trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(str)) return parseUtc(str);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  if (!str) return new Date();
  const parsed = new Date(str);
  return isNaN(parsed) ? new Date() : parsed;
};

// Returns "YYYY-MM-DD" in the user's local timezone — useful as a grouping key.
export const getLocalDateKey = (input) => {
  const date = resolveLocalDateFromInput(input);
  if (!date || isNaN(date)) return null;
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

// WhatsApp-style relative date label: "Today", "Yesterday", "N days ago" (up to 6),
// otherwise the full date in "MM/DD/YYYY".
export const getRelativeDateLabel = (input) => {
  const date = resolveLocalDateFromInput(input);
  if (!date || isNaN(date)) return "";
  const now = new Date();
  const msgStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round(
    (todayStart - msgStart) / (1000 * 60 * 60 * 24),
  );
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays > 1 && diffDays < 7) return `${diffDays} days ago`;
  return formatLocalDateMMDDYYYY(date);
};
